import type { Unsubscribe } from './disposable.js';

/**
 * CommandBus — semantic intentions decoupled from input devices (ADR 0032).
 *
 * WHY: views/components dispatch *what the operator means* ("go home", "switch
 * venture"), not raw DOM events. Any input source (pointer, keyboard, later
 * voice) translates device events into the SAME command, so behaviour is
 * device-agnostic and testable. Handlers are wired once in `wire-commands.ts`.
 *
 * The map grows per phase: P3 adds employee commands, P4 case/gate commands,
 * P5 leave, P7 letters — every new interaction enters HERE first.
 */
export interface CommandMap {
  /** Return to the Attention (home) view. */
  'nav.home': Record<string, never>;
  /** Leave this service for the OS front door — the Command Center (COMMAND-4, ADR 0110). */
  'nav.commandCenter': Record<string, never>;
  /** Open the People directory. */
  'people.open': Record<string, never>;
  /** Open one employee's profile. */
  'employee.open': { readonly employeeId: string };
  /** Switch the active venture (tenant) for the whole app. */
  'venture.switch': { readonly ventureId: string };
  /** Open the Cases list. */
  'cases.open': Record<string, never>;
  /** Open one case's detail/timeline. */
  'case.detail.open': { readonly caseId: string };
  /** Start a playbook execution for an employee (the agent takes over). */
  'case.open': { readonly playbookKey: string; readonly employeeId?: string };
  /** Run the compliance watchdog: expiring documents open renewal cases. */
  'compliance.scan': Record<string, never>;
  /** Switch the interface language (clean reload with the lang knob). */
  'locale.switch': { readonly locale: 'en' | 'ar' };
  /** File a leave request — the agent costs it and parks it at the gate. */
  'leave.request': { readonly employeeId: string; readonly startDate: string; readonly endDate: string };
  /** Request an HR letter — the agent drafts it bilingually for the gate. */
  'letter.request': { readonly employeeId: string; readonly letterKind: 'salary-certificate' | 'experience-certificate' };
  /** Resolve the pending human gate on a case. */
  'case.gate.approve': { readonly caseId: string; readonly note?: string };
  'case.gate.redo': { readonly caseId: string; readonly note?: string };
  'case.gate.reject': { readonly caseId: string; readonly note?: string };
}

export interface CommandBus {
  register<K extends keyof CommandMap>(name: K, handler: (payload: CommandMap[K]) => void): Unsubscribe;
  dispatch<K extends keyof CommandMap>(name: K, payload: CommandMap[K]): void;
}

type AnyHandler = (payload: unknown) => void;

export function createCommandBus(): CommandBus {
  const handlers = new Map<keyof CommandMap, Set<AnyHandler>>();
  return {
    register(name, handler) {
      let set = handlers.get(name);
      if (!set) {
        set = new Set();
        handlers.set(name, set);
      }
      const wrapped = handler as AnyHandler;
      set.add(wrapped);
      return () => set?.delete(wrapped);
    },
    dispatch(name, payload) {
      const set = handlers.get(name);
      if (!set) return;
      for (const handler of [...set]) handler(payload);
    },
  };
}
